import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment';

import Loading from "../../components/Loading";
import Data from '../../../repository';

import styles from '../../../styles/Admin.module.scss';

function Existing() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [refreshSetupId, setRefreshSetupId] = useState('');
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState(null);
    const [refreshSuccess, setRefreshSuccess] = useState(false);
    const [sumupRefreshSetupId, setSumupRefreshSetupId] = useState('');
    const [sumupRefreshing, setSumupRefreshing] = useState(false);
    const [sumupRefreshError, setSumupRefreshError] = useState(null);
    const [sumupRefreshSuccess, setSumupRefreshSuccess] = useState(false);
    const [retryingId, setRetryingId] = useState(null);
    const [retryResults, setRetryResults] = useState({});
    const [tokenRefreshId, setTokenRefreshId] = useState('');
    const [tokenRefreshSetupId, setTokenRefreshSetupId] = useState('');
    const [tokenRefreshing, setTokenRefreshing] = useState(false);
    const [tokenRefreshError, setTokenRefreshError] = useState(null);
    const [tokenRefreshSuccess, setTokenRefreshSuccess] = useState(false);
    const [cancellingId, setCancellingId] = useState(null);
    const [cancelError, setCancelError] = useState(null);

    useEffect(async () => {
        await refreshUser();
    }, []);

    const refreshUser = async () => {
        setLoading(true);
        const result = await Data.fetchUserById(id);
        setUser(result.data);
        setLoading(false);
    }

    const retryPayment = async (subscriptionId) => {
        setRetryingId(subscriptionId);
        setRetryResults(prev => ({ ...prev, [subscriptionId]: null }));
        try {
            const result = await Data.adminForceSubscriptionPayment(subscriptionId);
            setRetryResults(prev => ({ ...prev, [subscriptionId]: { success: result.data.outcome === 'authorized', message: result.data.outcome === 'authorized' ? 'Payment successful.' : `Payment declined: ${result.data.error || 'Unknown error'}` } }));
            if (result.data.outcome === 'authorized') await refreshUser();
        } catch (error) {
            setRetryResults(prev => ({ ...prev, [subscriptionId]: { success: false, message: error?.response?.data?.message || 'Could not process payment. Please try again.' } }));
        } finally {
            setRetryingId(null);
        }
    }

    const refreshPaymentStatus = async () => {
        if (!refreshSetupId.trim()) return;
        setRefreshing(true);
        setRefreshError(null);
        setRefreshSuccess(false);
        try {
            await Data.adminVerifyWorldpaySubscription(id, refreshSetupId.trim());
            setRefreshSuccess(true);
            setRefreshSetupId('');
            await refreshUser();
        } catch (error) {
            setRefreshError(error?.response?.data?.message || 'Could not verify payment status. Please try again.');
        } finally {
            setRefreshing(false);
        }
    }

    const refreshSumupPaymentStatus = async () => {
        if (!sumupRefreshSetupId.trim()) return;
        setSumupRefreshing(true);
        setSumupRefreshError(null);
        setSumupRefreshSuccess(false);
        try {
            await Data.adminVerifySumupSubscription(id, sumupRefreshSetupId.trim());
            setSumupRefreshSuccess(true);
            setSumupRefreshSetupId('');
            await refreshUser();
        } catch (error) {
            setSumupRefreshError(error?.response?.data?.message || 'Could not verify payment status. Please try again.');
        } finally {
            setSumupRefreshing(false);
        }
    }

    const refreshWorldpayToken = async () => {
        if (!tokenRefreshId.trim() || !tokenRefreshSetupId.trim()) return;
        setTokenRefreshing(true);
        setTokenRefreshError(null);
        setTokenRefreshSuccess(false);
        try {
            await Data.adminRefreshWorldpayToken(tokenRefreshId.trim(), tokenRefreshSetupId.trim());
            setTokenRefreshSuccess(true);
            setTokenRefreshId('');
            setTokenRefreshSetupId('');
        } catch (error) {
            setTokenRefreshError(error?.response?.data?.message || 'Could not refresh token. Please check the IDs and try again.');
        } finally {
            setTokenRefreshing(false);
        }
    };

    const cancelSubscription = async (sub) => {
        if (!window.confirm('Are you sure you want to cancel this membership immediately? This cannot be undone.')) return;
        setCancellingId(sub._id);
        setCancelError(null);
        try {
            if (sub._type === 'worldpay') {
                await Data.cancelWorldpaySubscription(sub._id, true);
            } else {
                await Data.cancelSumupSubscription(sub._id, true);
            }
            await refreshUser();
        } catch (err) {
            setCancelError(err?.response?.data?.message || 'Could not cancel membership. Please try again.');
        } finally {
            setCancellingId(null);
        }
    };

    if (loading || !user) return <Loading button={false} />;

    const subscriptions = [
        ...(user.worldpaySubscriptions || []).map(s => ({ ...s, _type: 'worldpay' })),
        ...(user.sumupSubscriptions || []).map(s => ({ ...s, _type: 'sumup' }))
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const activeSubscription = subscriptions.find(s => s.status === 'active' || s.status === 'past_due');
    const isOneTimeMembership = activeSubscription?.isRecurring === false;
    const activeSubType = activeSubscription?._type || null;

    return (
        <>
            <div className={styles.form}>
                <div>
                    <p>First Name:</p>
                    <input type="text" value={user.firstName || ''} disabled={true} />
                </div>

                <div>
                    <p>Last Name:</p>
                    <input type="text" value={user.lastName || ''} disabled={true} />
                </div>

                <div>
                    <p>Email Address:</p>
                    <input type="text" value={user.email || ''} disabled={true} />
                </div>

                <div>
                    <p>Phone Number:</p>
                    <input type="text" value={user.phoneNumber || '—'} disabled={true} />
                </div>

                <div>
                    <p>Verified:</p>
                    <input type="text" value={user.emailVerified ? 'Yes' : 'No'} disabled={true} />
                </div>

                <div>
                    <p>Admin:</p>
                    <input type="text" value={user.admin ? 'Yes' : 'No'} disabled={true} />
                </div>

                {user.personalAccessCode && (
                    <div>
                        <p>Personal Access Code:</p>
                        <input type="text" value={user.personalAccessCode} disabled={true} />
                    </div>
                )}

                <div>
                    <p>Membership:</p>
                    <input
                        type="text"
                        value={user.membership ? `${user.membership.plan} (${user.membership.status})` : 'No active membership'}
                        disabled={true}
                    />
                </div>

                {activeSubscription && (
                    <>
                        <div>
                            <p>Plan:</p>
                            <input type="text" value={activeSubscription.MembershipPlan?.name || '—'} disabled={true} />
                        </div>

                        <div>
                            <p>Amount:</p>
                            <input
                                type="text"
                                value={
                                    isOneTimeMembership
                                        ? `£${(activeSubscription.amount / 100).toFixed(2)} one-time`
                                        : `£${(activeSubscription.amount / 100).toFixed(2)} every ${activeSubscription.intervalCount} ${activeSubscription.interval}${activeSubscription.intervalCount > 1 ? 's' : ''}`
                                }
                                disabled={true}
                            />
                        </div>

                        <div>
                            <p>{isOneTimeMembership ? 'Membership Ends:' : 'Next Payment:'}</p>
                            <input
                                type="text"
                                value={
                                    isOneTimeMembership
                                        ? moment(activeSubscription.currentPeriodEnd).format('DD MMMM YYYY')
                                        : activeSubscription.nextBillingDate ? moment(activeSubscription.nextBillingDate).format('DD MMMM YYYY') : '—'
                                }
                                disabled={true}
                            />
                        </div>

                        <div>
                            <p>Payments Made:</p>
                            <input type="text" value={activeSubscription.paymentCount ?? '—'} disabled={true} />
                        </div>

                        {activeSubscription.MembershipPlan?.maxPayments && (
                            <div>
                                <p>Max Payments:</p>
                                <input type="text" value={activeSubscription.MembershipPlan.maxPayments} disabled={true} />
                            </div>
                        )}

                        {activeSubscription.MembershipPlan?.subscriptionEndDate && (
                            <div>
                                <p>Subscription Ends:</p>
                                <input type="text" value={moment(activeSubscription.MembershipPlan.subscriptionEndDate).format('DD MMMM YYYY')} disabled={true} />
                            </div>
                        )}
                    </>
                )}
            </div>

            {subscriptions.length > 0 && (
                <div className={styles.payments}>
                    <h2>Subscription History</h2>
                    <div>
                        {cancelError && <p style={{ color: '#c0392b', fontSize: 14, margin: '0 0 8px' }}>{cancelError}</p>}
                        {subscriptions.map((sub) => (
                            <div key={sub._id}>
                                <p>{sub.MembershipPlan?.name || 'Membership'}</p>
                                <p className={styles.status}>{sub.status}</p>
                                <p className={styles.amount}>£{(sub.amount / 100).toFixed(2)}</p>
                                <p style={{ gridColumn: '1 / -1', fontSize: 12, color: '#999', margin: 0, fontFamily: 'monospace' }}>ID: {sub._id}</p>
                                {(sub.status === 'active' || sub.status === 'past_due') && (
                                    <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                        {sub.status === 'past_due' && (
                                            <>
                                                <button
                                                    onClick={() => retryPayment(sub._id)}
                                                    disabled={retryingId === sub._id}
                                                    style={{ padding: '6px 14px', background: '#e67e22', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: retryingId === sub._id ? 0.6 : 1 }}
                                                >
                                                    {retryingId === sub._id ? 'Processing...' : 'Retry Payment'}
                                                </button>
                                                {retryResults[sub._id] && (
                                                    <p style={{ margin: 0, color: retryResults[sub._id].success ? '#27ae60' : '#c0392b', fontSize: 14 }}>
                                                        {retryResults[sub._id].message}
                                                    </p>
                                                )}
                                            </>
                                        )}
                                        <button
                                            onClick={() => cancelSubscription(sub)}
                                            disabled={cancellingId === sub._id}
                                            style={{ padding: '6px 14px', background: '#c0392b', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: cancellingId === sub._id ? 0.6 : 1 }}
                                        >
                                            {cancellingId === sub._id ? 'Cancelling...' : 'Cancel Membership'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {activeSubType !== 'sumup' && (
                <div className={styles.payments}>
                    <h2>Refresh Payment Status</h2>
                    <p style={{ marginTop: 8, color: '#666' }}>If a Worldpay payment was taken but the membership is not showing, enter the Setup ID or transaction reference below to verify it. One-time membership references can still be recovered after the pending setup expires.</p>
                    {refreshError && <p style={{ marginTop: 8, color: '#c0392b' }}>{refreshError}</p>}
                    {refreshSuccess && <p style={{ marginTop: 8, color: '#27ae60' }}>Membership successfully verified and activated.</p>}
                    <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '130px 1fr', alignItems: 'center', gap: 12 }}>
                        <p style={{ margin: 0 }}>Setup ID:</p>
                        <input
                            type="text"
                            value={refreshSetupId}
                            onChange={(e) => setRefreshSetupId(e.target.value)}
                            placeholder="e.g. sub_m_... or onetime_m_..."
                        />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <button
                            onClick={refreshPaymentStatus}
                            disabled={refreshing || !refreshSetupId.trim()}
                            style={{ padding: '10px 20px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (refreshing || !refreshSetupId.trim()) ? 0.6 : 1 }}
                        >
                            {refreshing ? 'Refreshing...' : 'Refresh Payment Status'}
                        </button>
                    </div>
                </div>
            )}

            {activeSubType !== 'sumup' && (
                <div className={styles.payments}>
                    <h2>Fix Recurring Payments (Refresh Worldpay Token)</h2>
                    <p style={{ marginTop: 8, color: '#666' }}>If a subscription shows "Payment declined" due to a missing Worldpay token, enter the Subscription ID and the original Transaction Reference (Setup ID) from the Worldpay portal to fix it.</p>
                    {tokenRefreshError && <p style={{ marginTop: 8, color: '#c0392b' }}>{tokenRefreshError}</p>}
                    {tokenRefreshSuccess && <p style={{ marginTop: 8, color: '#27ae60' }}>Token refreshed successfully. Recurring payments will now work for this subscription.</p>}
                    <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '160px 1fr', alignItems: 'center', gap: 12 }}>
                        <p style={{ margin: 0 }}>Subscription ID:</p>
                        <input
                            type="text"
                            value={tokenRefreshId}
                            onChange={(e) => setTokenRefreshId(e.target.value)}
                            placeholder="MongoDB subscription _id"
                        />
                        <p style={{ margin: 0 }}>Transaction Reference:</p>
                        <input
                            type="text"
                            value={tokenRefreshSetupId}
                            onChange={(e) => setTokenRefreshSetupId(e.target.value)}
                            placeholder="e.g. sub_m_..."
                        />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <button
                            onClick={refreshWorldpayToken}
                            disabled={tokenRefreshing || !tokenRefreshId.trim() || !tokenRefreshSetupId.trim()}
                            style={{ padding: '10px 20px', background: '#8e44ad', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (tokenRefreshing || !tokenRefreshId.trim() || !tokenRefreshSetupId.trim()) ? 0.6 : 1 }}
                        >
                            {tokenRefreshing ? 'Refreshing...' : 'Refresh Worldpay Token'}
                        </button>
                    </div>
                </div>
            )}

            {activeSubType === 'sumup' && (
                <div className={styles.payments}>
                    <h2>Refresh Payment Status</h2>
                    <p style={{ marginTop: 8, color: '#666' }}>If a SumUp payment was taken but the membership is not showing, enter the Setup ID below to verify and activate it.</p>
                    {sumupRefreshError && <p style={{ marginTop: 8, color: '#c0392b' }}>{sumupRefreshError}</p>}
                    {sumupRefreshSuccess && <p style={{ marginTop: 8, color: '#27ae60' }}>Membership successfully verified and activated.</p>}
                    <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '130px 1fr', alignItems: 'center', gap: 12 }}>
                        <p style={{ margin: 0 }}>Setup ID:</p>
                        <input
                            type="text"
                            value={sumupRefreshSetupId}
                            onChange={(e) => setSumupRefreshSetupId(e.target.value)}
                            placeholder="e.g. sumup_setup_..."
                        />
                    </div>
                    <div style={{ marginTop: 16 }}>
                        <button
                            onClick={refreshSumupPaymentStatus}
                            disabled={sumupRefreshing || !sumupRefreshSetupId.trim()}
                            style={{ padding: '10px 20px', background: '#f39c12', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', opacity: (sumupRefreshing || !sumupRefreshSetupId.trim()) ? 0.6 : 1 }}
                        >
                            {sumupRefreshing ? 'Refreshing...' : 'Refresh Payment Status'}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}

export default Existing;
